import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../../lib/api";
import { PageHeader, Card, Btn, Badge, Empty } from "../../components/UI";
import { ArrowLeft, Check, History, Clock3, Flame, BellRing, User } from "lucide-react";
import { toast } from "sonner";

export default function IntakeTaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activity, setActivity] = useState(null);

  const load = () => api.get(`/intake-tasks/${id}`).then((r) => setTask(r.data)).catch(() => setNotFound(true));
  useEffect(() => {
    load();
    api.get(`/intake-tasks/${id}/activity`).then((r) => setActivity(r.data.events)).catch(() => setActivity([]));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (notFound) return (
    <div className="py-16 text-center" data-testid="intake-task-notfound">
      <p className="text-sm text-[#7B6157]">This intake task could not be found.</p>
      <Btn className="mt-4" onClick={() => navigate("/engagement/intake-tasks")}>Back to Intake Tasks</Btn>
    </div>
  );
  if (!task) return <div className="py-16 text-center text-sm text-[#84675B]">Loading intake task…</div>;

  const done = task.status === "completed";
  const overdue = !done && task.due_date && task.due_date < dayjs().format("YYYY-MM-DD");
  const overdueDays = overdue ? dayjs().startOf("day").diff(dayjs(task.due_date), "day") : 0;
  const daysOpen = task.created_at ? dayjs().diff(dayjs(task.created_at), "day") : 0;

  const complete = async () => {
    try { await api.put(`/intake-tasks/${id}/complete`); toast.success("Intake task completed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="intake-task-detail-page">
      <button data-testid="intake-task-back" onClick={() => navigate("/engagement/intake-tasks")}
        className="mb-3 flex items-center gap-1.5 text-xs font-bold text-[#7B6157] hover:text-[#4E342E]"><ArrowLeft size={13} /> Back to Intake Tasks</button>
      <PageHeader title="Intake Task" subtitle={task.title}
        action={!done && <Btn data-testid="intake-task-complete-btn" className="bg-[#2E7D32] hover:bg-[#1B5E20]" onClick={complete}><Check size={14} /> Mark Complete</Btn>} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge value={done ? "completed" : task.status} />
        <Badge value={task.priority} />
        {overdue && <span className="inline-flex items-center gap-1 rounded-full bg-[#FFEBEE] px-2.5 py-1 text-[10px] font-bold text-[#C62828]" data-testid="intake-task-overdue-chip"><Flame size={10} /> {overdueDays} DAY{overdueDays === 1 ? "" : "S"} OVERDUE</span>}
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="intake-task-stats">
        {[["Days open", daysOpen, `since ${dayjs(task.created_at).format("MMM D")}`],
          ["Due date", task.due_date ? dayjs(task.due_date).format("MM/DD/YYYY") : "-", null],
          ["Assignee", task.assignee_name || "Unassigned", null],
          ["Activity events", activity === null ? "…" : activity.length, null]].map(([l, v, sub]) => (
          <Card key={l} className="p-4">
            <div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{l}</div>
            <div className="mt-0.5 font-heading text-xl font-bold text-[#3E2723]">{v}</div>
            {sub && <div className="text-[11px] text-[#7B6157]">{sub}</div>}
          </Card>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card className="p-5" data-testid="intake-task-info">
          <h3 className="mb-3 font-heading text-sm font-bold text-[#3E2723]">Details</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            {[["Assignee", task.assignee_name ? <span className="inline-flex items-center gap-1"><User size={12} />{task.assignee_name}</span> : "Unassigned"],
              ["Priority", task.priority],
              ["Reminder", task.reminder_date ? <span className="inline-flex items-center gap-1"><BellRing size={12} />{dayjs(task.reminder_date).format("MM/DD/YYYY")}</span> : "None"],
              ["Created", task.created_at ? dayjs(task.created_at).format("MM/DD/YYYY h:mm A") : "-"],
              ["Completed", task.completed_at ? dayjs(task.completed_at).format("MM/DD/YYYY h:mm A") : "-"]].map(([l, v]) => (
              <div key={l}><div className="text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{l}</div><div className="capitalize text-[#3E2723]">{v}</div></div>
            ))}
          </div>
          {task.notes && <div className="mt-4 rounded-xl bg-[#F5F2EB] px-4 py-3 text-sm leading-relaxed text-[#4E342E]" data-testid="intake-task-notes">{task.notes}</div>}
        </Card>
        <Card className="p-5" data-testid="intake-task-activity">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><History size={14} /> Audit Trail</h3>
          {activity === null ? <div className="py-8 text-center text-sm text-[#84675B]">Loading…</div> :
            activity.length === 0 ? <Empty text="No recorded activity yet" /> : (
              <div className="relative space-y-3 before:absolute before:left-[7px] before:top-1 before:bottom-1 before:w-px before:bg-[#E5DFD3]">
                {activity.map((e, i) => (
                  <div key={i} className="relative pl-6" data-testid={`intake-task-event-${i}`}>
                    <span className={`absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white ${e.action === "create" ? "bg-[#2E7D32]" : e.action === "complete" ? "bg-[#1565C0]" : "bg-[#8D6E63]"}`} />
                    <div className="text-sm"><span className="font-semibold capitalize text-[#3E2723]">{e.action}</span><span className="text-[#5D4037]"> by {e.by}</span></div>
                    {e.detail && <div className="text-xs text-[#5D4037]">{e.detail}</div>}
                    <div className="flex items-center gap-1 text-[11px] text-[#84675B]"><Clock3 size={10} />{dayjs(e.at).format("MMM D, YYYY h:mm A")}</div>
                  </div>
                ))}
              </div>
            )}
        </Card>
      </div>
    </div>
  );
}
